/**
 * The landing screen: what the company owes, what it is owed, and what is
 * waiting for somebody to act.
 *
 * Three rules keep it honest:
 *   - Every tile is one published read. Nothing is aggregated here that the
 *     server did not already total, and a keyset page — which has no count —
 *     reports "20+" rather than a number nobody computed.
 *   - A tile whose permission the operator lacks is not rendered at all. It
 *     never issues its request either, so the screen cannot 403 in pieces.
 *   - Every tile links to the screen that owns the work. The dashboard is a
 *     way in, not a second place to do the job.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  CalendarCheck,
  ClipboardCheck,
  ShoppingCart,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { useSession } from "@/lib/session/use-session";
import { formatMoney } from "@/lib/money/money";
import { MoneyDisplay } from "@/components/money/money-display";
import { formatCalendarDate } from "@/lib/format";

import {
  ATTENTION_PAGE,
  getAPAging,
  getARAging,
  listOpenFiscalPeriods,
  listOpenReorderSuggestions,
  listPurchaseOrdersAwaitingApproval,
  listRecentSalesOrders,
  RECENT_PAGE,
  type Money,
} from "./api";

/**
 * The aging reports require one currency — an aging column may only ever hold
 * a single one — and it is the company's base currency, which the session
 * publishes. `fallbackCurrency` labels an empty sum, where there is no row to
 * take a currency from.
 */
function sum(values: readonly Money[], fallbackCurrency: string): Money {
  // Money is an exact decimal string; adding it as a float is how a ledger
  // ends up off by a halala. Scale to minor units through BigInt instead.
  let total = 0n;
  let scale = 0;
  for (const value of values) {
    const fraction = value.amount.split(".")[1] ?? "";
    scale = Math.max(scale, fraction.length);
  }
  for (const value of values) {
    const negative = value.amount.startsWith("-");
    const [whole, fraction = ""] = value.amount.replace("-", "").split(".");
    const scaled = BigInt(whole + fraction.padEnd(scale, "0"));
    total += negative ? -scaled : scaled;
  }
  const text = total
    .toString()
    .replace("-", "")
    .padStart(scale + 1, "0");
  const amount =
    scale === 0 ? text : `${text.slice(0, text.length - scale)}.${text.slice(text.length - scale)}`;
  return {
    amount: `${total < 0n ? "-" : ""}${amount}`,
    currency: values[0]?.currency ?? fallbackCurrency,
  };
}

export interface DashboardPageProps {
  /**
   * The company's base currency, from the session. The aging tiles are read
   * in one currency, and it is the ledger's rather than a guess.
   */
  currency: string;
  /**
   * Navigate to a tile's screen. The page stays router-free — it renders real
   * anchors, so it works (and is keyboard/middle-click friendly) without a
   * router; the route file intercepts the click for SPA navigation.
   */
  onOpen?: (path: string) => void;
}

export function DashboardPage({ currency, onOpen }: DashboardPageProps) {
  const { t, i18n } = useTranslation();
  const { user, hasPermission } = useSession();
  const locale = i18n.language;

  const canReadAR = hasPermission("finance.ar-aging.read");
  const canReadAP = hasPermission("finance.ap-aging.read");
  const canReadPO = hasPermission("purchasing.purchase_order.read");
  const canReadReorder = hasPermission("purchasing.reorder_suggestion.read");
  const canReadSales = hasPermission("sales.order.read");
  const canReadPeriods = hasPermission("ledger.fiscal-period.read");

  const ar = useQuery({
    queryKey: ["dashboard", "ar-aging", currency],
    enabled: canReadAR && currency !== "",
    queryFn: ({ signal }) => getARAging(currency, signal),
  });
  const ap = useQuery({
    queryKey: ["dashboard", "ap-aging", currency],
    enabled: canReadAP && currency !== "",
    queryFn: ({ signal }) => getAPAging(currency, signal),
  });
  const approvals = useQuery({
    queryKey: ["dashboard", "po-approvals"],
    enabled: canReadPO,
    queryFn: ({ signal }) => listPurchaseOrdersAwaitingApproval(signal),
  });
  const reorder = useQuery({
    queryKey: ["dashboard", "reorder"],
    enabled: canReadReorder,
    queryFn: ({ signal }) => listOpenReorderSuggestions(signal),
  });
  const sales = useQuery({
    queryKey: ["dashboard", "sales"],
    enabled: canReadSales,
    queryFn: ({ signal }) => listRecentSalesOrders(signal),
  });
  const periods = useQuery({
    queryKey: ["dashboard", "periods"],
    enabled: canReadPeriods,
    queryFn: ({ signal }) => listOpenFiscalPeriods(signal),
  });

  // Overdue is everything that is not in the "current" bucket.
  const arOverdue =
    ar.data === undefined
      ? undefined
      : sum(
          [
            ar.data.buckets["1_30"],
            ar.data.buckets["31_60"],
            ar.data.buckets["61_90"],
            ar.data.buckets["90_plus"],
          ],
          currency,
        );
  const apOverdue =
    ap.data === undefined
      ? undefined
      : sum(
          ap.data.buckets.filter((b) => b.bucket !== "current").map((b) => b.amount),
          currency,
        );

  /**
   * A keyset page publishes no total, so a full page with a cursor after it
   * can only honestly say "at least this many". The bound is the limit that
   * was actually requested — quoting any other number would be a guess.
   */
  const countOf = (
    page: { items: readonly unknown[]; next_cursor?: string | null } | undefined,
    limit: number,
  ) =>
    page === undefined ? undefined : page.next_cursor ? `${limit}+` : String(page.items.length);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("dashboard.greeting", { name: user?.display_name ?? "" })}
        </h1>
        <p className="text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {canReadAR ? (
          <Tile
            icon={ArrowDownLeft}
            label={t("dashboard.receivables")}
            to="/finance/reports/aging"
            onOpen={onOpen}
            value={ar.data ? formatMoney(ar.data.total, locale) : undefined}
            failed={ar.isError}
            note={
              arOverdue
                ? t("dashboard.overdue", { amount: formatMoney(arOverdue, locale) })
                : undefined
            }
          />
        ) : null}

        {canReadAP ? (
          <Tile
            icon={ArrowUpRight}
            label={t("dashboard.payables")}
            to="/finance/reports/aging"
            onOpen={onOpen}
            value={ap.data ? formatMoney(ap.data.total, locale) : undefined}
            failed={ap.isError}
            note={
              apOverdue
                ? t("dashboard.overdue", { amount: formatMoney(apOverdue, locale) })
                : undefined
            }
          />
        ) : null}

        {canReadPO ? (
          <Tile
            icon={ClipboardCheck}
            label={t("dashboard.awaitingApproval")}
            to="/purchasing/purchase-orders"
            onOpen={onOpen}
            value={countOf(approvals.data, ATTENTION_PAGE)}
            failed={approvals.isError}
            note={t("dashboard.awaitingApprovalNote")}
          />
        ) : null}

        {canReadReorder ? (
          <Tile
            icon={AlertTriangle}
            label={t("dashboard.reorder")}
            to="/purchasing/reorder-suggestions"
            onOpen={onOpen}
            value={countOf(reorder.data, ATTENTION_PAGE)}
            failed={reorder.isError}
            note={t("dashboard.reorderNote")}
          />
        ) : null}

        {canReadPeriods ? (
          <Tile
            icon={CalendarCheck}
            label={t("dashboard.openPeriods")}
            to="/ledger/fiscal-periods"
            onOpen={onOpen}
            value={countOf(periods.data, RECENT_PAGE)}
            failed={periods.isError}
            note={
              periods.data?.items[0]
                ? t("dashboard.periodEnds", {
                    date: formatCalendarDate(periods.data.items[0].ends_on, locale),
                  })
                : undefined
            }
          />
        ) : null}
      </div>

      {canReadSales ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart className="size-4" aria-hidden="true" />
              {t("dashboard.recentOrders")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sales.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {t("errors.generic")}
              </p>
            ) : sales.isPending ? (
              <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
            ) : sales.data.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("table.empty")}</p>
            ) : (
              <ul className="divide-y">
                {sales.data.items.map((order) => (
                  <li key={order.id} className="flex items-center gap-3 py-2 text-sm">
                    {/* A draft imported from a channel may not have been
                        numbered yet. */}
                    <span className="font-mono text-xs">{order.order_number ?? "—"}</span>
                    <Badge variant="secondary">{t(`status.${order.status}`, order.status)}</Badge>
                    <span className="ms-auto tabular-nums">
                      <MoneyDisplay value={order.grand_total} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      <p className="text-xs text-muted-foreground">
        {t("dashboard.currencyNote", { currency: currency })}
      </p>
    </div>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  note,
  to,
  failed,
  onOpen,
}: {
  icon: LucideIcon;
  label: string;
  value: string | undefined;
  note?: string;
  to: string;
  failed: boolean;
  onOpen?: (path: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <a
      href={to}
      className="rounded-lg outline-none focus-visible:ring-2"
      onClick={(event) => {
        // Let the browser handle a modified click — an operator opening a tile
        // in a new tab means it.
        if (onOpen === undefined || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        onOpen(to);
      }}
    >
      <Card className="h-full transition-colors hover:bg-muted/40">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Icon className="size-4" aria-hidden="true" />
            {label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-semibold tabular-nums">
            {failed ? (
              <span className="text-base font-normal text-destructive">{t("errors.generic")}</span>
            ) : value === undefined ? (
              <span className="text-base font-normal text-muted-foreground">
                {t("common.loading")}
              </span>
            ) : (
              value
            )}
          </p>
          {note !== undefined && !failed ? (
            <p className="pt-1 text-xs text-muted-foreground">{note}</p>
          ) : null}
        </CardContent>
      </Card>
    </a>
  );
}
